import React, { useRef, useState } from "react";
import { EditOutlined, SearchOutlined } from "@ant-design/icons";
import { Layout, Tooltip, Tabs } from "antd";
import * as style from "./style.less";
import Preview from "./assets/Apercu.svg";
import Rhese from "./assets/Rhesage.svg";
import Formatting from "./assets/MiseEnPage.svg";
import Definitions from "./assets/Definitions.svg";
import Dialogues from "./assets/Dialogues.svg";
import Syllables from "./assets/Syllabation.svg";
import LogoFrog from "./assets/LogoFrog.svg";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Spinner } from "../../";
import { useI18n } from "../../../../../i18n";
import { EDITOR_TABS_MAP } from "../../../config";
import { useProject } from "../Page";
import ProjectPanel from "../ProjectPanel/ProjectPanel";
import { getTabItem, TabItems } from "../../../styles/antDesignTheme";

const { Header } = Layout;

type Props = {
  projectId: string;
  loading: boolean;
  toggleSearch: () => void;
  toggleEdition: () => void;
  isSearchOpen: boolean;
};

export default function ActionsBar({ projectId, toggleSearch, toggleEdition }: Props) {
  const { project, bookLoading: loading } = useProject({ projectId });
  const location = useLocation();
  const navigate = useNavigate();
  const ref = useRef(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const lastPart = (location.pathname.match(/[^/]*$/) || [""])[0];

  const [t] = useI18n();
  const i18nKey = "project.actionBar.default.tabPaneTitle";

  const navigateToTab = (tabPath: string) => {
    navigate({
      pathname: window.location.pathname.replace(/[^/]*$/, tabPath),
      search: window.location.search,
      hash: window.location.hash,
    });
  };

  if (!loading && !project) return <Navigate to="/" />;
  if (!project) {
    return (
      <div className={style.wrapper}>
        <Header id={style.header}><Spinner /></Header>
      </div>
    );
  }

  const tabItems: TabItems = [
    getTabItem({
      key: "1",
      label: (
        <Tooltip
          getPopupContainer={() => ref.current || document.body}
          title={t(`${i18nKey}.preview`)}>
          <Preview />
        </Tooltip>
      ),
    }),
    getTabItem({
      key: "2",
      label: (
        <Tooltip
          getPopupContainer={() => ref.current || document.body}
          title={t(`${i18nKey}.rhese`)}>
          <Rhese />
        </Tooltip>
      ),
    }),
    getTabItem({
      key: "3",
      label: (
        <Tooltip
          getPopupContainer={() => ref.current || document.body}
          title={t(`${i18nKey}.formatting`)}>
          <Formatting />
        </Tooltip>
      ),
    }),
    getTabItem({
      key: "4",
      label: (
        <Tooltip
          getPopupContainer={() => ref.current || document.body}
          title={t(`${i18nKey}.definitions`)}>
          <Definitions />
        </Tooltip>
      ),
    }),
    getTabItem({
      key: "5",
      disabled: true,
      label: (
        <Tooltip
          getPopupContainer={() => ref.current || document.body}
          title={t(`${i18nKey}.dialogues`)}>
          <Dialogues style={{ opacity: 0.5 }} />
        </Tooltip>
      ),
    }),
    getTabItem({
      key: "6",
      label: (
        <Tooltip
          getPopupContainer={() => ref.current || document.body}
          title={t(`${i18nKey}.syllables`)}>
          <Syllables />
        </Tooltip>
      ),
    }),
  ];

  const content = (
    <>
      <nav className={style.nav}>
        <Link className={style.logo} to="/">
          <LogoFrog />
        </Link>
        {">"}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsPanelOpen(!isPanelOpen);
          }}>
          {project.name}
        </button>
      </nav>
      <div ref={ref} style={{ position: "absolute", width: "100%" }}></div>
      <Tabs
        defaultActiveKey={EDITOR_TABS_MAP[lastPart] || "1"}
        tabPosition="bottom"
        id={style.tabs}
        onChange={(key) => {
          switch (key) {
            case "1":
              return navigateToTab("overview");
            case "2":
              return navigateToTab("rhese");
            case "3":
              return navigateToTab("format");
            case "4":
              return navigateToTab("definition");
            case "5":
              return navigateToTab("dialogue");
            case "6":
              return navigateToTab("syllable");
            case "7":
              return navigateToTab("narration");
          }
        }}
        centered
        items={tabItems}
      />
      <div className={style.search}>
        <div onClick={toggleEdition}>
          <EditOutlined style={{ fontSize: "2em" }} />
        </div>
        <div onClick={toggleSearch}>
          <SearchOutlined style={{ fontSize: "2em" }} />
        </div>
      </div>
    </>
  );

  return (
    <div className={style.wrapper}>
      <ProjectPanel
        projectId={projectId}
        close={() => setIsPanelOpen(false)}
        isVisible={isPanelOpen}
      />
      <Header id={style.header}>{content}</Header>
    </div>
  );
}
